import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import {
  CreatePipelineRequest,
  DagStatus,
  DeletePipelineRequest,
  DeployState,
  EditingContext,
  PipelineConflictResponse,
  PipelinePrResult,
  PromoteResult,
  PromoteStatus,
  RunSummary,
  SavePipelineRequest,
} from '@internal/backstage-plugin-sql-pipeline-common';

/** 저장 중 다른 사람이 같은 파일을 바꿨다. 최신 설정을 함께 들고 있다. */
export class PipelineConflictError extends Error {
  readonly response: PipelineConflictResponse;
  constructor(response: PipelineConflictResponse) {
    super(response.message);
    this.name = 'PipelineConflictError';
    this.response = response;
  }
}

export interface SqlPipelineApi {
  /** 신규 생성. 이름 중복은 서버가 저장소를 보고 막는다. */
  createPipeline(req: CreatePipelineRequest): Promise<PipelinePrResult>;
  /** 수정 폼 초기값. 설정은 카탈로그 투영이 아니라 Git 에서 온다. */
  getEditingContext(pipelineName: string): Promise<EditingContext>;
  /** @throws PipelineConflictError 저장 직전 파일이 바뀐 경우 */
  savePipeline(
    pipelineName: string,
    req: SavePipelineRequest,
  ): Promise<PipelinePrResult>;
  deletePipeline(
    pipelineName: string,
    req: DeletePipelineRequest,
  ): Promise<PipelinePrResult>;
  /** 삭제 확인용 최근 실행 요약. 조회 실패 시 unavailable 가 채워진다. */
  getRunSummary(
    pipelineName: string,
    opts?: { env?: 'dev' | 'prod'; days?: number },
  ): Promise<RunSummary>;
  /** Airflow DAG 상태. DAG 미등록도 unavailable 로 정상 응답한다. */
  getDagStatus(
    pipelineName: string,
    env: 'dev' | 'prod',
  ): Promise<DagStatus>;
  /** 운영계 반영 가능 여부. */
  getPromoteStatus(pipelineName: string): Promise<PromoteStatus>;
  /** 목록용 반영 상태. PR 을 찾지 않아 promote-status 보다 가볍다. */
  getDeployState(pipelineName: string): Promise<DeployState>;
  /** 운영계 반영 요청 — 해당 PR 에 `/promote` 코멘트를 남긴다. */
  requestPromote(pipelineName: string): Promise<PromoteResult>;
}

export const sqlPipelineApiRef = createApiRef<SqlPipelineApi>({
  id: 'plugin.sql-pipeline.service',
});

/**
 * pluginId 문자열이 등장하는 **유일한 위치**.
 *
 * 이전에는 모든 컴포넌트가 discoveryApi + fetchApi 를 직접 조합해
 * `getBaseUrl('airflow-api')` 같은 문자열을 각자 들고 있었다.
 */
const PLUGIN_ID = 'sql-pipeline';

export class SqlPipelineClient implements SqlPipelineApi {
  constructor(
    private readonly deps: { discoveryApi: DiscoveryApi; fetchApi: FetchApi },
  ) {}

  private async base(): Promise<string> {
    return this.deps.discoveryApi.getBaseUrl(PLUGIN_ID);
  }

  private async readError(res: Response): Promise<never> {
    let detail = '';
    try {
      const body = await res.json();
      detail = body?.error?.message ?? body?.message ?? body?.error ?? '';
    } catch {
      detail = await res.text().catch(() => '');
    }
    throw new Error(detail || `요청이 실패했습니다 (HTTP ${res.status})`);
  }

  async createPipeline(req: CreatePipelineRequest): Promise<PipelinePrResult> {
    const res = await this.deps.fetchApi.fetch(`${await this.base()}/pipelines`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    });
    if (!res.ok) return this.readError(res);
    return res.json();
  }

  async getEditingContext(pipelineName: string): Promise<EditingContext> {
    const res = await this.deps.fetchApi.fetch(
      `${await this.base()}/pipelines/${encodeURIComponent(
        pipelineName,
      )}/editing-context`,
    );
    if (!res.ok) return this.readError(res);
    return res.json();
  }

  async savePipeline(
    pipelineName: string,
    req: SavePipelineRequest,
  ): Promise<PipelinePrResult> {
    const res = await this.deps.fetchApi.fetch(
      `${await this.base()}/pipelines/${encodeURIComponent(pipelineName)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(req),
      },
    );
    if (res.status === 409) {
      throw new PipelineConflictError(await res.json());
    }
    if (!res.ok) return this.readError(res);
    return res.json();
  }

  async getRunSummary(
    pipelineName: string,
    opts: { env?: 'dev' | 'prod'; days?: number } = {},
  ): Promise<RunSummary> {
    const params = new URLSearchParams({
      env: opts.env ?? 'prod',
      days: String(opts.days ?? 7),
    });
    const res = await this.deps.fetchApi.fetch(
      `${await this.base()}/pipelines/${encodeURIComponent(
        pipelineName,
      )}/runs?${params.toString()}`,
    );
    if (!res.ok) return this.readError(res);
    return res.json();
  }

  async getDagStatus(
    pipelineName: string,
    env: 'dev' | 'prod',
  ): Promise<DagStatus> {
    const res = await this.deps.fetchApi.fetch(
      `${await this.base()}/pipelines/${encodeURIComponent(
        pipelineName,
      )}/dag-status?env=${env}`,
    );
    if (!res.ok) return this.readError(res);
    return res.json();
  }

  async getPromoteStatus(pipelineName: string): Promise<PromoteStatus> {
    const res = await this.deps.fetchApi.fetch(
      `${await this.base()}/pipelines/${encodeURIComponent(
        pipelineName,
      )}/promote-status`,
    );
    if (!res.ok) return this.readError(res);
    return res.json();
  }

  async getDeployState(pipelineName: string): Promise<DeployState> {
    const res = await this.deps.fetchApi.fetch(
      `${await this.base()}/pipelines/${encodeURIComponent(
        pipelineName,
      )}/deploy-state`,
    );
    if (!res.ok) return this.readError(res);
    return (await res.json()).state;
  }

  async requestPromote(pipelineName: string): Promise<PromoteResult> {
    const res = await this.deps.fetchApi.fetch(
      `${await this.base()}/pipelines/${encodeURIComponent(
        pipelineName,
      )}/promote`,
      { method: 'POST' },
    );
    if (!res.ok) return this.readError(res);
    return res.json();
  }

  async deletePipeline(
    pipelineName: string,
    req: DeletePipelineRequest,
  ): Promise<PipelinePrResult> {
    const res = await this.deps.fetchApi.fetch(
      `${await this.base()}/pipelines/${encodeURIComponent(pipelineName)}`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(req),
      },
    );
    if (!res.ok) return this.readError(res);
    return res.json();
  }
}
